import {computed, ref, type Ref} from 'vue';
import {formatEnum} from '@/lib/format';
import {getCountryInfo} from '@/lib/country';
import { PLATFORM_LABELS } from '@/lib/platforms';
import type { GithubRunnerScope, RunnerPlatform } from '@/api/runners';

export interface FilterOption {
  value: string;
  label: string;
}

export interface FilterableRunner {
  status: string;
  /** Null for GitHub runners. */
  executor: string | null;
  countryCode: string;
  platform?: RunnerPlatform;
  // Additional fields used for text search
  name?: string;
  productId?: string;
  city?: string;
  ipv4?: string;
  ipv6?: string;
  concurrency?: number | null;
  specs?: {
    chip?: string;
    vcpu?: number | string;
    ram?: string;
    nvmeSsd?: string | number;
  };
  gitlabHost?: string;
  github?: GithubRunnerScope | null;
}

export interface FilterState {
  statuses: string[];
  executors: string[];
  locations: string[];
  /** Missing in filters saved before GitHub runners existed. */
  platforms?: string[];
}

const FILTER_STORAGE_KEY = 'runner-dashboard-filters';

export function loadFiltersFromStorage(): FilterState {
  try {
    const raw = localStorage.getItem(FILTER_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      return {
        statuses: Array.isArray(parsed.statuses) ? parsed.statuses : [],
        executors: Array.isArray(parsed.executors) ? parsed.executors : [],
        locations: Array.isArray(parsed.locations) ? parsed.locations : [],
        platforms: Array.isArray(parsed.platforms) ? parsed.platforms : [],
      };
    }
  } catch { /* ignore corrupt storage */ }
  return { statuses: [], executors: [], locations: [], platforms: [] };
}

export function saveFiltersToStorage(state: FilterState): void {
  try {
    localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(state));
  } catch { /* quota exceeded etc. */ }
}

/** Dashboard statuses a runner can reach, in lifecycle order. */
export const RUNNER_STATUSES = [
  'creating',
  'configuring',
  'active',
  'pausing',
  'paused',
  'invalid_gitlab_token',
  'registration_failed',
  'unknown',
] as const;

export type RunnerStatus = (typeof RUNNER_STATUSES)[number];

export const STATUS_OPTIONS: FilterOption[] = RUNNER_STATUSES.map((s) => ({
  value: s,
  label: formatEnum(s),
}));

export function deriveExecutorOptions(runners: FilterableRunner[]): FilterOption[] {
  const unique = new Set(
    runners
      .map((r) => r.executor)
      .filter((e): e is string => !!e && e.trim().length > 0)
  );
  return Array.from(unique)
    .sort()
    .map((e) => ({ value: e, label: e }));
}

export function derivePlatformOptions(runners: FilterableRunner[]): FilterOption[] {
  const unique = new Set(runners.map((r) => r.platform ?? 'GITLAB'));
  return (Object.keys(PLATFORM_LABELS) as RunnerPlatform[])
    .filter((platform) => unique.has(platform))
    .map((platform) => ({ value: platform, label: PLATFORM_LABELS[platform] }));
}

export function deriveLocationOptions(runners: FilterableRunner[]): FilterOption[] {
  const unique = new Set(
    runners
      .map((r) => r.countryCode)
      .filter((c) => !!c && c.trim().length > 0)
  );
  return Array.from(unique)
    .sort()
    .map((code) => {
      const info = getCountryInfo(code);
      return { value: code, label: `${info.flag} ${info.name}` };
    });
}

/**
 * Filter runners based on active filter selections.
 * If no filters are selected for a dimension, all runners pass that dimension.
 */
export function filterRunners<T extends FilterableRunner>(
  runners: T[],
  filters: FilterState,
): T[] {
  let result = runners;
  if (filters.statuses.length > 0) {
    const set = new Set(filters.statuses);
    result = result.filter((r) => set.has(r.status));
  }
  if (filters.executors.length > 0) {
    const set = new Set(filters.executors);
    result = result.filter((r) => r.executor !== null && set.has(r.executor));
  }
  if (filters.locations.length > 0) {
    const set = new Set(filters.locations);
    result = result.filter((r) => set.has(r.countryCode));
  }
  if (filters.platforms && filters.platforms.length > 0) {
    const set = new Set(filters.platforms);
    result = result.filter((r) => set.has(r.platform ?? 'GITLAB'));
  }
  return result;
}

/**
 * Filter runners by a free-text search query.
 * Matches case-insensitively against all visible card fields.
 */
export function textSearchRunners<T extends FilterableRunner>(
  runners: T[],
  query: string,
): T[] {
  const q = query.trim().toLowerCase();
  if (!q) return runners;

  return runners.filter((r) => {
    const countryInfo = r.countryCode ? getCountryInfo(r.countryCode) : undefined;
    const searchable = [
      r.name,
      r.productId,
      r.status,
      r.executor,
      r.countryCode,
      countryInfo?.name,
      r.city,
      r.ipv4,
      r.ipv6,
      r.gitlabHost,
      r.github?.owner,
      r.github?.repository ?? undefined,
      r.github?.runnerGroup ?? undefined,
      r.specs?.chip,
      r.specs?.vcpu != null ? String(r.specs.vcpu) : undefined,
      r.specs?.ram,
      r.specs?.nvmeSsd != null ? String(r.specs.nvmeSsd) : undefined,
      r.concurrency != null ? String(r.concurrency) : undefined,
    ];
    return searchable.some((val) => val && val.toLowerCase().includes(q));
  });
}

/**
 * Prune selected filter values that no longer exist in available options.
 */
export function pruneFilters(
  filters: FilterState,
  options: {
    statuses: FilterOption[];
    executors: FilterOption[];
    locations: FilterOption[];
    platforms?: FilterOption[];
  }
): FilterState {
  const validStatuses = new Set(options.statuses.map((o) => o.value));
  const validExecutors = new Set(options.executors.map((o) => o.value));
  const validLocations = new Set(options.locations.map((o) => o.value));
  const validPlatforms = new Set((options.platforms ?? []).map((o) => o.value));
  return {
    statuses: filters.statuses.filter((v) => validStatuses.has(v)),
    executors: filters.executors.filter((v) => validExecutors.has(v)),
    locations: filters.locations.filter((v) => validLocations.has(v)),
    platforms: (filters.platforms ?? []).filter((v) => validPlatforms.has(v)),
  };
}

/**
 * Vue composable for runner filtering with localStorage persistence.
 */
export function useRunnerFilters<T extends FilterableRunner>(runners: Ref<T[]>) {
  const stored = loadFiltersFromStorage();
  const selectedStatuses = ref<string[]>(stored.statuses);
  const selectedExecutors = ref<string[]>(stored.executors);
  const selectedLocations = ref<string[]>(stored.locations);
  const selectedPlatforms = ref<string[]>(stored.platforms ?? []);
  const searchQuery = ref<string>('');

  const executorOptions = computed(() => deriveExecutorOptions(runners.value));
  const locationOptions = computed(() => deriveLocationOptions(runners.value));
  const platformOptions = computed(() => derivePlatformOptions(runners.value));

  function save() {
    saveFiltersToStorage({
      statuses: selectedStatuses.value,
      executors: selectedExecutors.value,
      locations: selectedLocations.value,
      platforms: selectedPlatforms.value,
    });
  }

  function pruneSelected() {
    const pruned = pruneFilters(
      {
        statuses: selectedStatuses.value,
        executors: selectedExecutors.value,
        locations: selectedLocations.value,
        platforms: selectedPlatforms.value,
      },
      {
        statuses: STATUS_OPTIONS,
        executors: executorOptions.value,
        locations: locationOptions.value,
        platforms: platformOptions.value,
      }
    );
    selectedStatuses.value = pruned.statuses;
    selectedExecutors.value = pruned.executors;
    selectedLocations.value = pruned.locations;
    selectedPlatforms.value = pruned.platforms ?? [];
  }

  const filteredRunners = computed(() => {
    const afterDropdowns = filterRunners(runners.value, {
      statuses: selectedStatuses.value,
      executors: selectedExecutors.value,
      locations: selectedLocations.value,
      platforms: selectedPlatforms.value,
    });
    return textSearchRunners(afterDropdowns, searchQuery.value);
  });

  return {
    selectedStatuses,
    selectedExecutors,
    selectedLocations,
    selectedPlatforms,
    searchQuery,
    statusOptions: STATUS_OPTIONS,
    executorOptions,
    locationOptions,
    platformOptions,
    filteredRunners,
    save,
    pruneSelected,
  };
}
